import { useState } from 'react';
import { Card } from '../components/ui/card';
import { X, ChevronLeft, ChevronRight, Camera } from 'lucide-react';
import SEOHead, { pageSEO, generateBreadcrumbSchema } from '../components/SEOHead';

const GalleryPage = () => {
    const [selectedImage, setSelectedImage] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    const images = [
        {
            src: 'https://customer-assets.emergentagent.com/job_e7b0ebde-43af-4d29-9e43-ebf077dcc0a4/artifacts/7jbokqd6_DSCF6976.JPG',
            alt: 'Wire-haired Vizsla ready for mantrailing session with long tracking leash in Wiltshire woodland'
        },
        {
            src: 'https://customer-assets.emergentagent.com/job_e7b0ebde-43af-4d29-9e43-ebf077dcc0a4/artifacts/1se8an19_DSCF6850.JPG',
            alt: 'German Shepherd enjoying scent work training with handlers near Malmesbury'
        },
        {
            src: 'https://customer-assets.emergentagent.com/job_e7b0ebde-43af-4d29-9e43-ebf077dcc0a4/artifacts/gc7opa89_DSCF6853.JPG',
            alt: 'German Shepherd and handler during mantrailing session by mossy tree in Wiltshire countryside'
        },
        {
            src: 'https://customer-assets.emergentagent.com/job_e7b0ebde-43af-4d29-9e43-ebf077dcc0a4/artifacts/riscni1q_DSCF6896.JPG',
            alt: 'Handler running with dog through woodland mantrailing trail near Malmesbury'
        },
        {
            src: 'https://customer-assets.emergentagent.com/job_e7b0ebde-43af-4d29-9e43-ebf077dcc0a4/artifacts/r4s78we3_DSCF6920.JPG',
            alt: 'Belgian Malinois tracking human scent on forest trail during Craven Canines session'
        },
        {
            src: 'https://customer-assets.emergentagent.com/job_e7b0ebde-43af-4d29-9e43-ebf077dcc0a4/artifacts/5hrv8qss_DSCF7052.JPG',
            alt: 'Wire-haired Vizsla following scent trail through branches in mantrailing training'
        },
        {
            src: 'https://customer-assets.emergentagent.com/job_e7b0ebde-43af-4d29-9e43-ebf077dcc0a4/artifacts/4mdlv6sc_DSCF7065.JPG',
            alt: 'Dog receiving reward treat from handler after successful mantrailing find'
        },
        {
            src: 'https://customer-assets.emergentagent.com/job_e7b0ebde-43af-4d29-9e43-ebf077dcc0a4/artifacts/ej8tksxm_DSCF7094.JPG',
            alt: 'White Samoyed running happily through forest during scent work session in Wiltshire'
        },
        {
            src: 'https://customer-assets.emergentagent.com/job_e7b0ebde-43af-4d29-9e43-ebf077dcc0a4/artifacts/lxji64r3_DSCF7153.JPG',
            alt: 'Golden Retriever with handler enjoying autumn mantrailing session near Malmesbury'
        },
        {
            src: 'https://customer-assets.emergentagent.com/job_e7b0ebde-43af-4d29-9e43-ebf077dcc0a4/artifacts/l1z792fc_DSCF6993.JPG',
            alt: 'Handler preparing wire-haired dog with tracking harness and long line for mantrailing'
        },
        {
            src: 'https://customer-assets.emergentagent.com/job_e7b0ebde-43af-4d29-9e43-ebf077dcc0a4/artifacts/9ud6xg5y_DSCF7169.JPG',
            alt: 'Golden Retriever successfully finding hidden person during mantrailing exercise'
        },
        {
            src: 'https://customer-assets.emergentagent.com/job_e7b0ebde-43af-4d29-9e43-ebf077dcc0a4/artifacts/e568iz7q_DSCF7207.JPG',
            alt: 'Handler presenting scent article to dog at start of mantrailing trail'
        },
        {
            src: 'https://customer-assets.emergentagent.com/job_e7b0ebde-43af-4d29-9e43-ebf077dcc0a4/artifacts/sgbwibt5_DSCF7209.JPG',
            alt: 'Small black dog with orange tracking harness ready for scent work session'
        },
        {
            src: 'https://customer-assets.emergentagent.com/job_e7b0ebde-43af-4d29-9e43-ebf077dcc0a4/artifacts/2ckpocw0_DSCF7223.JPG',
            alt: 'Dog receiving reward from handler after completing mantrailing trail in Wiltshire woodland'
        },
        {
            src: 'https://customer-assets.emergentagent.com/job_e7b0ebde-43af-4d29-9e43-ebf077dcc0a4/artifacts/at6w31pw_DSCF7273.JPG',
            alt: 'Wire-haired Vizsla running trail through pine forest during advanced mantrailing session'
        }
    ];

    const openLightbox = (index) => {
        setCurrentIndex(index);
        setSelectedImage(images[index]);
    };

    const closeLightbox = () => {
        setSelectedImage(null);
    };

    const goToPrevious = (e) => {
        e.stopPropagation();
        const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
        setCurrentIndex(newIndex);
        setSelectedImage(images[newIndex]);
    };

    const goToNext = (e) => {
        e.stopPropagation();
        const newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
        setCurrentIndex(newIndex);
        setSelectedImage(images[newIndex]);
    };

    return (
        <div className="min-h-screen" data-testid="gallery-page">
            <SEOHead 
                title={pageSEO.gallery.title}
                description={pageSEO.gallery.description}
                keywords={pageSEO.gallery.keywords}
                canonical={pageSEO.gallery.canonical}
                schema={generateBreadcrumbSchema([
                    { name: 'Home', url: '/' },
                    { name: 'Gallery', url: '/gallery' }
                ])}
            />
            {/* Hero Section */}
            <section className="relative py-20 md:py-28 bg-primary">
                <div className="container-custom relative z-10">
                    <div className="max-w-3xl">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                                <Camera className="w-6 h-6 text-white" />
                            </div>
                            <span className="text-white/90 font-medium text-lg">Craven Canines</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight font-serif">
                            Mantrailing Photo Gallery
                        </h1>
                        <p className="text-xl text-white/90 leading-relaxed">
                            Real dogs, real sessions, real results. Explore moments from our mantrailing 
                            sessions in the beautiful Wiltshire countryside near Malmesbury.
                        </p>
                    </div>
                </div>
            </section>

            {/* Gallery Grid */}
            <section className="section-spacing">
                <div className="container-custom">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {images.map((image, index) => (
                            <Card 
                                key={index}
                                className="overflow-hidden cursor-pointer group"
                                onClick={() => openLightbox(index)}
                                data-testid={`gallery-image-${index}`}
                            >
                                <div className="relative aspect-square overflow-hidden">
                                    <img
                                        src={image.src}
                                        alt={image.alt}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Lightbox Modal */}
            {selectedImage && (
                <div 
                    className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
                    onClick={closeLightbox}
                    data-testid="lightbox-modal"
                >
                    {/* Close Button */}
                    <button
                        onClick={closeLightbox}
                        className="absolute top-4 right-4 text-white/80 hover:text-white p-2 transition-colors"
                        data-testid="lightbox-close"
                    >
                        <X className="w-8 h-8" />
                    </button>

                    {/* Previous Button */}
                    <button
                        onClick={goToPrevious}
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 bg-black/30 rounded-full transition-colors"
                        data-testid="lightbox-prev"
                    >
                        <ChevronLeft className="w-8 h-8" />
                    </button>

                    {/* Image */}
                    <img
                        src={selectedImage.src}
                        alt={selectedImage.alt}
                        className="max-h-[85vh] max-w-[90vw] object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />

                    {/* Next Button */}
                    <button
                        onClick={goToNext}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 bg-black/30 rounded-full transition-colors"
                        data-testid="lightbox-next"
                    >
                        <ChevronRight className="w-8 h-8" />
                    </button>

                    {/* Image Counter */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm">
                        {currentIndex + 1} / {images.length}
                    </div>
                </div>
            )}
        </div>
    );
};

export default GalleryPage;
